import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Schools from "@/pages/Schools";
import UsersPage from "@/pages/Users";
import AcademicConfig from "@/pages/AcademicConfig";
import Teachers from "@/pages/Teachers";
import Classes from "@/pages/Classes";
import Students from "@/pages/Students";
import StudentProfile from "@/pages/StudentProfile";
import MyDiary from "@/pages/MyDiary";
import DiaryWorkspace from "@/pages/DiaryWorkspace";
import DiaryPanel from "@/pages/DiaryPanel";
import Importacao from "@/pages/Importacao";
import Disciplinar from "@/pages/Disciplinar";
import Excelencia from "@/pages/Excelencia";
import Premiacoes from "@/pages/Premiacoes";
import Relatorios from "@/pages/Relatorios";
import Auditoria from "@/pages/Auditoria";
import Automacoes from "@/pages/Automacoes";
import TreinamentoIA from "@/pages/TreinamentoIA";
import Integracoes from "@/pages/Integracoes";
import AlertasFaltas from "@/pages/AlertasFaltas";
import Landing from "@/pages/Landing";
import PendenciasNoa from "@/pages/PendenciasNoa";
import Reunioes from "@/pages/Reunioes";
import NotasFaltasEditor from "@/pages/NotasFaltasEditor";
import QuadroHorarios from "@/pages/QuadroHorarios";
import HorarioProfessor from "@/pages/HorarioProfessor";
import Presenca from "@/pages/Presenca";
import Kiosk from "@/pages/Kiosk";
import PainelMerenda from "@/merenda/Painel";
import ConsumoDia from "@/merenda/ConsumoDia";
import EstoqueMerenda from "@/merenda/Estoque";
import NotasMerenda from "@/merenda/Notas";
import CardapioMerenda from "@/merenda/Cardapio";
import AlimentosMerenda from "@/merenda/Alimentos";
import ImportarMerenda from "@/merenda/Importar";
import ComprasMerenda from "@/merenda/Compras";
import { MANAGERS, MERENDA_ROLES } from "@/merenda/common";
import { Loader2, ShieldAlert } from "lucide-react";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="grid min-h-screen place-items-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="grid min-h-screen place-items-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  if (user && roles.includes(user.role)) return children;
  return (
    <div className="grid min-h-[60vh] place-items-center px-4 text-center" data-testid="no-access">
      <div>
        <ShieldAlert className="mx-auto mb-3 h-12 w-12 text-slate-300" />
        <h2 className="font-display text-xl font-bold text-slate-800">Sem acesso</h2>
        <p className="mt-1 text-sm text-slate-500">Você não tem permissão para acessar esta área.</p>
      </div>
    </div>
  );
}

const GESTAO = ["SUPERADMIN", "DIRECAO", "COORDENACAO"];

function Home() {
  const { user } = useAuth();
  return user?.role === "COZINHEIRA" ? <Navigate to="/merenda/consumo" replace /> : <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/quiosque" element={<Kiosk />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route element={<Protected><AppLayout /></Protected>}>
              <Route path="/dashboard" element={<Home />} />
              <Route path="/merenda" element={<RoleGuard roles={MANAGERS}><PainelMerenda /></RoleGuard>} />
              <Route path="/merenda/consumo" element={<RoleGuard roles={MERENDA_ROLES}><ConsumoDia /></RoleGuard>} />
              <Route path="/merenda/estoque" element={<RoleGuard roles={MERENDA_ROLES}><EstoqueMerenda /></RoleGuard>} />
              <Route path="/merenda/cardapio" element={<RoleGuard roles={MERENDA_ROLES}><CardapioMerenda /></RoleGuard>} />
              <Route path="/merenda/notas" element={<RoleGuard roles={MANAGERS}><NotasMerenda /></RoleGuard>} />
              <Route path="/merenda/alimentos" element={<RoleGuard roles={MANAGERS}><AlimentosMerenda /></RoleGuard>} />
              <Route path="/merenda/importar" element={<RoleGuard roles={MANAGERS}><ImportarMerenda /></RoleGuard>} />
              <Route path="/merenda/compras" element={<RoleGuard roles={MANAGERS}><ComprasMerenda /></RoleGuard>} />
              <Route path="/escolas" element={<RoleGuard roles={["SUPERADMIN", "DIRECAO"]}><Schools /></RoleGuard>} />
              <Route path="/usuarios" element={<RoleGuard roles={GESTAO}><UsersPage /></RoleGuard>} />
              <Route path="/configuracoes-academicas" element={<RoleGuard roles={GESTAO}><AcademicConfig /></RoleGuard>} />
              <Route path="/professores" element={<Teachers />} />
              <Route path="/turmas" element={<Classes />} />
              <Route path="/alunos" element={<Students />} />
              <Route path="/alunos/:id" element={<StudentProfile />} />
              <Route path="/meu-diario" element={<MyDiary />} />
              <Route path="/diario" element={<DiaryWorkspace />} />
              <Route path="/acompanhamento-diarios" element={<RoleGuard roles={GESTAO}><DiaryPanel /></RoleGuard>} />
              <Route path="/importacao" element={<Importacao />} />
              <Route path="/disciplinar" element={<Disciplinar />} />
              <Route path="/excelencia" element={<RoleGuard roles={GESTAO}><Excelencia /></RoleGuard>} />
              <Route path="/premiacoes" element={<RoleGuard roles={GESTAO}><Premiacoes /></RoleGuard>} />
              <Route path="/relatorios" element={<Relatorios />} />
              <Route path="/turmas/:id/notas-faltas" element={<NotasFaltasEditor />} />
              <Route path="/horario-professor" element={<HorarioProfessor />} />
              <Route path="/quadro-horarios" element={<QuadroHorarios />} />
              <Route path="/alertas-faltas" element={<RoleGuard roles={GESTAO}><AlertasFaltas /></RoleGuard>} />
              <Route path="/pendencias-noa" element={<RoleGuard roles={GESTAO}><PendenciasNoa /></RoleGuard>} />
              <Route path="/reunioes" element={<RoleGuard roles={GESTAO}><Reunioes /></RoleGuard>} />
              <Route path="/reunioes/:id" element={<RoleGuard roles={GESTAO}><Reunioes /></RoleGuard>} />
              <Route path="/automacoes" element={<RoleGuard roles={["SUPERADMIN"]}><Automacoes /></RoleGuard>} />
              <Route path="/treinamento-ia" element={<RoleGuard roles={["SUPERADMIN"]}><TreinamentoIA /></RoleGuard>} />
              <Route path="/integracoes" element={<RoleGuard roles={["SUPERADMIN"]}><Integracoes /></RoleGuard>} />
              <Route path="/auditoria" element={<RoleGuard roles={GESTAO}><Auditoria /></RoleGuard>} />
              <Route path="/presenca" element={<RoleGuard roles={["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR"]}><Presenca /></RoleGuard>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
